/**
 * Which clients a member can see.
 *
 * TWO MODES, AND THE DIFFERENCE THAT MATTERS IS FUTURE CLIENTS.
 *
 *   All clients      - everything, including any customer onboarded later.
 *   Only these       - a fixed list. A customer added next month is NOT
 *                      included until somebody adds them here.
 *
 * That second sentence is the whole reason this screen says it out loud.
 * "Restricted to five clients" and "restricted to whichever five existed in
 * August" look identical on a settings page and behave very differently six
 * months later - somebody quietly stops seeing half their accounts, or keeps
 * seeing accounts they should not.
 *
 * Owners and admins cannot be scoped at all: they administer the organisation,
 * so a limit they could lift themselves in two clicks is not a limit. The
 * server refuses it; this explains why rather than just disabling the control.
 */

import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

interface Client { id: string; name: string }

export type ScopeMode = 'all' | 'include' | 'exclude';

export function ClientScope({ orgId, uid, email, role, mode: initialMode, clientIds, onSaved }: {
  orgId: string;
  uid: string;
  email: string | null;
  role: string;
  mode: ScopeMode;
  clientIds: string[];
  onSaved: () => void;
}) {
  const [clients, setClients] = useState<Client[]>([]);
  const [mode, setMode] = useState<ScopeMode>(initialMode);
  // Kept as a name because the rest of the component reads far better for it.
  const all = mode === 'all';
  const [picked, setPicked] = useState<Set<string>>(new Set(clientIds));
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    httpsCallable(functions, 'listClients')({ orgId })
      .then((r) => setClients(((r.data as { clients: Client[] }).clients ?? []).map(
        (c) => ({ id: c.id, name: c.name }),
      )))
      .catch(() => { /* the list is a convenience; the modes still work */ });
  }, [orgId]);

  const unscopable = role === 'owner' || role === 'admin';

  const shown = search.trim()
    ? clients.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : clients;

  const toggle = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await httpsCallable(functions, 'setMemberClients')({
        orgId, uid, mode, viewAllClients: all, clientIds: [...picked],
      });
      const who = email ?? 'They';
      setMsg(
        mode === 'all'
          ? `${who} can see all clients, including any added later.`
          : mode === 'exclude'
            ? `${who} can see every client except ${picked.size}. Clients added later WILL be included.`
            : `${who} can see ${picked.size} client${picked.size === 1 ? '' : 's'}. Clients added later will NOT be included.`
      );
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that.');
    } finally { setBusy(false); }
  };

  return (
    <div className="scope-editor" style={{ padding: '8px 0 4px' }}>
      {err && <div className="banner banner-error">{err}</div>}
      {msg && <div className="banner banner-ok">{msg}</div>}

      {unscopable ? (
        <p className="panel-note" style={{ margin: 0 }}>
          {role === 'owner' ? 'Owners' : 'Admins'} manage the whole organisation, so their view
          cannot be limited to particular clients. Change the role first if that is what you want.
        </p>
      ) : (
        <>
          <label className="check-row">
            <input type="radio" checked={mode === 'all'} onChange={() => setMode('all')} />
            <span>
              <strong>All clients</strong>
              <span className="dim"> - including any client added later</span>
            </span>
          </label>
          {/*
            THE MODE THAT WAS MISSING. "Hide our own servers, show every
            customer" cannot be said with an include list: it decays the moment
            a new client is onboarded, and nobody notices until somebody is
            missing a customer they were supposed to be watching.
          */}
          <label className="check-row">
            <input type="radio" checked={mode === 'exclude'} onChange={() => setMode('exclude')} />
            <span>
              <strong>All clients except the ones I pick</strong>
              <span className="dim"> - clients added later ARE included. Use this to hide your own
                internal systems while covering every customer, now and in future.</span>
            </span>
          </label>
          <label className="check-row">
            <input type="radio" checked={mode === 'include'} onChange={() => setMode('include')} />
            <span>
              <strong>Only the clients I pick</strong>
              <span className="dim"> - a client added later will NOT be included until you add them here</span>
            </span>
          </label>

          {!all && (
            <>
              {clients.length > 8 && (
                <input
                  className="field-input" style={{ marginTop: 6 }}
                  placeholder="Find a client…"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                />
              )}
              <div style={{
                maxHeight: 180, overflowY: 'auto', marginTop: 6,
                border: '1px solid var(--border)', borderRadius: 6, padding: 6,
              }}>
                {clients.length === 0 && (
                  <div className="dim" style={{ fontSize: 12 }}>
                    No clients yet. Map some under Integrations first.
                  </div>
                )}
                {shown.map((c) => (
                  <label className="check-row" key={c.id} style={{ margin: '2px 0', fontSize: 12 }}>
                    <input type="checkbox" checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
              <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
                {mode === 'exclude'
                  ? `${picked.size} hidden. Everything else is visible, including clients added later `
                    + 'and machines not yet matched to one. Up to 10 can be excluded.'
                  : `${picked.size} selected. Machines not yet matched to a client are visible only to `
                    + 'people who can see all clients.'}
              </div>
            </>
          )}

          <button
            className="btn btn-primary" style={{ marginTop: 8 }}
            disabled={busy || (!all && picked.size === 0) || (mode === 'exclude' && picked.size > 10)}
            title={
              !all && picked.size === 0 ? 'Pick at least one client, or choose All clients'
                : mode === 'exclude' && picked.size > 10
                  ? 'Up to 10 clients can be excluded - past that, pick the ones they SHOULD see'
                  : ''
            }
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save access'}
          </button>
        </>
      )}
    </div>
  );
}
